<script setup lang="ts">
import { ref, watch } from 'vue';
import type { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkQrCodeEditablePermission } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../../composables/useLocale';
import { usePermissions } from '../../composables/usePermissions';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useCreateLoginCards } from '../LoginCard/useCreateLoginCards';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';

import Modal from '../Modal.vue';
import EditGroupCode from './EditGroupCode.vue';
import EditGroupPassword from './EditGroupPassword.vue';
import PermissionLock from '../PermissionLock.vue';

export type Props = {
  name: ModalTypes;
  group: CustomerMgmtTypes.GetGroupResponse;
};
const props = defineProps<Props>();
const emit = defineEmits<{
  (event: 'groupUpdated', value: string): void;
}>();

const { t } = useLocale();
const { isOpen, close, open } = useModal(props.name);
const { organization } = useCurrentOrganization();
const { createLoginCardsForGroupUsers } = useCreateLoginCards();
const { permissionContext, getCallerAssociation, reporterWithReason } = usePermissions();

const inProgress = ref(false);
const editGroupCodeRef = ref<InstanceType<typeof EditGroupCode> | null>(null);

const openCreateNewLoginCardsModal = () => {
  close();
  createLoginCardsForGroupUsers(props.group.uid, {
    onClose: () => {
      open();
    },
  });
};

const removeGroupcode = () => {
  editGroupCodeRef.value?.removeGroupcode();
};

const qrCodeEditabilityCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: false, reason: 'not_permitted' });
watch(
  [props.group, organization],
  async () => {
    if (!organization.value) {
      qrCodeEditabilityCheck.value = { allowed: false, reason: 'not_permitted' };
    }

    checkQrCodeEditablePermission(
      permissionContext.value,
      { organization: organization.value, role: 'student' },
      getCallerAssociation,
      reporterWithReason,
    ).then((result) => {
      qrCodeEditabilityCheck.value = result;
    });
  },
  { immediate: true },
);

defineExpose({
  removeGroupcode,
});
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.group_login')"
    :is-open="isOpen"
    show-close-btn
    width="560"
    :action="close"
    :action-label="t('lcp.done')"
    :loading="inProgress || editGroupCodeRef?.isUpdating"
  >
    <v-row class="ga-6" no-gutters>
      <v-col cols="12">
        <EditGroupCode ref="editGroupCodeRef" :group="group" @group-updated="() => emit('groupUpdated', group.uid)" />
      </v-col>
      <v-col cols="12">
        <v-divider></v-divider>
      </v-col>
      <v-col cols="12">
        <EditGroupPassword :group="group" />
      </v-col>
      <v-col cols="12">
        <v-divider></v-divider>
      </v-col>
      <v-col cols="12">
        <v-row no-gutters>
          <v-col cols="12" class="d-flex ga-2 align-center pb-4">
            <span class="font-weight-bold">{{ t('lcp.create_new_login_cards') }}</span>
          </v-col>
          <v-col cols="12" class="d-flex ga-2 align-center pb-4">
            <p>
              {{ t('lcp.create_login_card_warning') }}
            </p>
          </v-col>
          <v-col cols="12" class="d-flex ga-2">
            <v-row>
              <v-col cols="auto">
                <v-chip
                  @click="openCreateNewLoginCardsModal"
                  tag="create_qr_code_for_users"
                  :disabled="!qrCodeEditabilityCheck.allowed"
                >
                  {{ t('lcp.create_new_login_cards') }}
                  <template #prepend>
                    <v-icon class="mr-2" style="transform: scaleX(-1)">mdi-qrcode</v-icon>
                  </template>
                </v-chip>
              </v-col>
              <v-col v-if="!qrCodeEditabilityCheck.allowed" cols="auto" class="d-flex align-center justify-end pl-0">
                <PermissionLock color="primary" :reason="qrCodeEditabilityCheck.reason" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </Modal>
</template>
